// src/pages/Home/PolitiqueConfidentialitePage.jsx
// ou components/PolitiqueConfidentialitePage.jsx selon ton emplacement

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBalanceScale,
  FaCookieBite,
  FaEnvelope,
  FaExclamationTriangle,
  FaFileAlt,
  FaGlobe,
  FaLock,
  FaPrint,
  FaShieldAlt,
  FaUserShield,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScollToTop";
import { getSettings } from "../../services/settingsServices";

const DEFAULT_SETTINGS = {
  siteweb_club: "",
  nom_club: "",
  adresse_club: "",
  responsable_club: "",
  mail_club: "",
  siret_club: "",
};

const safeValue = (value, fallback = "Non renseigné") => {
  const clean = String(value || "").trim();
  return clean || fallback;
};

const InfoCard = ({ icon: Icon, label, value, href }) => {
  if (!value) return null;

  const content = (
    <div className="group flex h-full items-start gap-4 rounded-3xl border border-slate-200 bg-white/85 p-5 shadow-sm backdrop-blur-xl transition hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:border-white/10 dark:bg-white/5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-primary/20">
        <Icon />
      </div>

      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
          {label}
        </p>

        <p className="mt-2 break-words text-sm font-bold leading-6 text-slate-800 dark:text-slate-100">
          {value}
        </p>
      </div>
    </div>
  );

  if (!href) return content;

  const isExternal = /^https?:\/\//i.test(href);

  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className="block h-full"
    >
      {content}
    </a>
  );
};

const PrivacySection = ({ number, title, children }) => {
  return (
    <section className="rounded-[1.7rem] border border-slate-200 bg-white/80 p-5 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5 sm:p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-sm font-extrabold text-white shadow-lg">
          {number}
        </div>

        <div className="min-w-0">
          <h2 className="font-barlow text-xl font-extrabold leading-tight text-slate-950 dark:text-white sm:text-2xl">
            {title}
          </h2>

          <div className="mt-4 space-y-4 text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base sm:leading-8">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
};

const PolitiqueSkeleton = () => {
  return (
    <main className="min-h-screen bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] dark:bg-slate-950">
      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-9 w-52 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-32 animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>

        <div className="mt-10 space-y-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="h-36 animate-pulse rounded-[1.7rem] bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>
      </div>
    </main>
  );
};

const PolitiqueConfidentialitePage = () => {
  useScrollToTop();

  const navigate = useNavigate();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...(clubSettings || {}),
        });
      } catch (error) {
        console.error(
          "Erreur lors de la récupération de la politique de confidentialité :",
          error
        );

        if (mounted) {
          setErrorMsg("Impossible de charger les informations du club.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const siteName = safeValue(settings.siteweb_club, "site internet du club");
  const clubName = safeValue(settings.nom_club, "Le club");
  const publicationManager = safeValue(settings.responsable_club);
  const clubEmail = safeValue(settings.mail_club);

  const siteHref = useMemo(() => {
    const site = String(settings.siteweb_club || "").trim();

    if (!site) return "";

    if (/^https?:\/\//i.test(site)) return site;

    return `https://${site}`;
  }, [settings.siteweb_club]);

  const emailHref = settings.mail_club ? `mailto:${settings.mail_club}` : "";

  const handlePrint = () => {
    window.print();
  };

  if (loading) return <PolitiqueSkeleton />;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <style>{`
        @media print {
          nav,
          footer,
          .no-print {
            display: none !important;
          }

          main {
            padding-top: 0 !important;
            background: #ffffff !important;
          }

          .print-card {
            box-shadow: none !important;
            border: none !important;
          }
        }
      `}</style>

      <div className="relative mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="no-print mb-8 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            <FaArrowLeft className="text-xs" />
            Retour
          </button>

          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
          >
            <FaPrint className="text-sm" />
            Imprimer
          </button>
        </div>

        <section className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaShieldAlt />
            Textes réglementaires
          </div>

          <h1 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Politique de{" "}
            <span className="text-primary">confidentialité</span>
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Cette page présente la manière dont le site du club peut collecter,
            utiliser et protéger certaines informations liées à la navigation ou
            aux demandes effectuées via le site.
          </p>

          <p className="mt-4 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
            {clubName}
          </p>
        </section>

        {errorMsg && (
          <div className="mt-10 rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
            {errorMsg}
          </div>
        )}

        <section className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <InfoCard
            icon={FaGlobe}
            label="Site concerné"
            value={siteName}
            href={siteHref}
          />

          <InfoCard
            icon={FaLock}
            label="Responsable"
            value={clubName}
          />

          <InfoCard
            icon={FaUserShield}
            label="Publication"
            value={publicationManager}
          />

          <InfoCard
            icon={FaEnvelope}
            label="Contact données"
            value={clubEmail}
            href={emailHref}
          />
        </section>

        <article className="print-card mt-10 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 p-5 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90 sm:p-7 lg:p-8">
          <div className="mb-8 rounded-[2rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <FaFileAlt />
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                  Engagement de confidentialité
                </p>

                <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
                  Le club s’engage à utiliser les informations transmises via le
                  site uniquement dans le cadre des services proposés et des
                  demandes adressées par les visiteurs.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <PrivacySection number="1" title="Introduction">
              <p>
                Chez <strong>{clubName}</strong>, la confidentialité des
                visiteurs est importante. Cette politique de confidentialité
                présente les types d’informations pouvant être collectées par le
                site <strong>{siteName}</strong> et la manière dont elles peuvent
                être utilisées.
              </p>
            </PrivacySection>

            <PrivacySection number="2" title="Fichiers journaux">
              <p>
                Comme de nombreux sites internet, <strong>{siteName}</strong>{" "}
                peut utiliser des fichiers journaux. Ces fichiers peuvent
                enregistrer certaines informations techniques liées à la
                navigation, telles que l’adresse IP, le type de navigateur, le
                fournisseur d’accès, la date et l’heure de consultation, les
                pages visitées ou les pages de provenance.
              </p>

              <p>
                Ces informations sont principalement utilisées pour analyser les
                tendances, administrer le site, suivre son bon fonctionnement et
                améliorer l’expérience des visiteurs. Elles ne sont pas utilisées
                pour identifier personnellement les visiteurs.
              </p>
            </PrivacySection>

            <PrivacySection number="3" title="Cookies et technologies similaires">
              <p>
                <strong>{siteName}</strong> peut utiliser des cookies afin de
                faciliter la navigation, mémoriser certaines préférences ou
                mesurer la fréquentation du site. Un cookie est un petit fichier
                enregistré sur le terminal de l’utilisateur lors de sa visite.
              </p>

              <p>
                L’utilisateur peut configurer son navigateur pour accepter,
                refuser ou supprimer les cookies. Le refus de certains cookies
                peut toutefois limiter l’accès à certaines fonctionnalités du
                site.
              </p>
            </PrivacySection>

            <PrivacySection number="4" title="Données transmises volontairement">
              <p>
                Certaines informations personnelles peuvent être transmises
                volontairement par l’utilisateur, notamment lorsqu’il utilise un
                formulaire de contact, une demande d’inscription ou tout autre
                service proposé par le site.
              </p>

              <p>
                Ces informations peuvent notamment inclure le nom, le prénom,
                l’adresse email, le numéro de téléphone ou le contenu du message
                envoyé. Elles sont utilisées uniquement pour répondre à la
                demande de l’utilisateur ou assurer le fonctionnement du service
                concerné.
              </p>
            </PrivacySection>

            <PrivacySection number="5" title="Protection des données personnelles">
              <p>
                Conformément à la réglementation applicable, l’utilisateur
                dispose de droits d’accès, de rectification, d’effacement,
                d’opposition et de limitation du traitement relatifs à ses
                données personnelles.
              </p>

              <p>
                Ces droits peuvent être exercés en contactant le club à l’adresse
                suivante : <strong>{clubEmail}</strong>.
              </p>

              <p>
                Pour toute information complémentaire ou réclamation,
                l’utilisateur peut également contacter la Commission Nationale de
                l’Informatique et des Libertés, accessible depuis le site{" "}
                <a
                  href="https://www.cnil.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-primary hover:underline"
                >
                  www.cnil.fr
                </a>
                .
              </p>
            </PrivacySection>

            <PrivacySection number="6" title="Durée de conservation">
              <p>
                Les données transmises via le site sont conservées pendant la
                durée nécessaire au traitement de la demande ou au respect des
                obligations applicables à l’association.
              </p>

              <p>
                Lorsque les données ne sont plus nécessaires, elles peuvent être
                supprimées ou archivées conformément aux règles internes du club.
              </p>
            </PrivacySection>

            <PrivacySection number="7" title="Partage des informations">
              <p>
                <strong>{clubName}</strong> ne vend pas les données personnelles
                des utilisateurs. Certaines informations peuvent toutefois être
                transmises aux personnes habilitées du club ou à des prestataires
                techniques uniquement lorsque cela est nécessaire au
                fonctionnement du site ou au traitement d’une demande.
              </p>
            </PrivacySection>

            <PrivacySection number="8" title="Consentement">
              <p>
                En utilisant le site <strong>{siteName}</strong>, l’utilisateur
                reconnaît avoir pris connaissance de la présente politique de
                confidentialité et en accepte les termes.
              </p>
            </PrivacySection>
          </div>

          <div
            aria-hidden
            className="mt-8 h-1.5 w-full rounded-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
          />
        </article>

        <div className="no-print mt-8 rounded-[1.5rem] border border-amber-200 bg-amber-50 p-4 text-sm leading-7 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <div className="flex items-start gap-3">
            <FaExclamationTriangle className="mt-1 shrink-0" />
            <p>
              Ce modèle constitue une base de présentation. Pour une conformité
              RGPD complète, il est recommandé de l’adapter aux traitements
              réellement effectués par le club et aux outils utilisés sur le
              site.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PolitiqueConfidentialitePage;